import { defineEffect } from '../../../core/effect';
import { seeded, seg, tween } from '../../../core/effect/motion';
import {
  add, canvasOf, cardName, fade, fill, place, playerColor, sprite,
} from '../fx/common';
import {
  bankFrame, formulaRow, materialCards, monsterPlate, sideRow,
} from '../fx/materialBank';

/**
 * **融合召喚の変身バンク**（Issue #599 v1。指示書 `../direction/material_summon_fusion.md`。キービジュアルは無い＝方向は人が決めた）。
 * 疑似イベント `material_summon`（`editorPorts/materialSummon.js`）の状況 `material_summon_fusion`。**motion なし**（盤は触らない。
 * 素材はこの前の move でもう墓地、出るモンスターは後の `spsummoning` で盤に出る＝召喚バンクと 2 回あってよい）。
 * 骨組み（並び・数式・同時の変身・モンスターの板・はけ）はシンクロ（`material_summon_synchro_effect.js`）と共有（`../fx/materialBank.js`）。
 *
 * ```
 * 0      溜め: 舞台を暗く。召喚した人の側から素材のカード（表）が飛んできて、画面の中央を渦のために空けて並ぶ（1 枚目は左・ほかは右）
 * 380    数式 ○○ + △△ を左から叩き込む（素材の名前。〜700）。**カードは見えたまま**で、名前を叩くたびに対応するカードが揺れて硬い縁が光る
 * 1000   **同時に変身**: 画面の中央に渦（赤と青の硬い腕が互い違いに巻く 2 枚の螺旋＋紫の硬い輪）が開く。カードの形の閃光・ヒットストップ 100ms・揺れ
 * 1100   **渦に吸い込まれる**: 素材のカードが渦の周りを回りながら（公転・小さく・傾く）中心へ（〜1600）。後ろに赤・青の硬い線の軌跡。数式は渦へ吸われる
 * 1600   **衝突**: 中心でぶつかる → カードの形の閃光 2 フレーム・画面の閃光 1 フレーム・ヒットストップ 100ms・揺れ。
 *        1700 渦が一気に縮んで弾け（白い硬い輪が広がる）、鋼の破片と放射の硬い線
 * 1750   中心から出るモンスター: 鋼の板とイラストが縦の細い隙間から開き、影 → 色。1950 名前、2030 FUSION SUMMON の帯（縁はプレイヤーの色と紫）
 *        （出るモンスターがまだ入力されていなければ衝突で終わる。尺も 2400 に縮む）
 * 2800   全部が縦に細く潰れて消え、暗幕を戻す。何も残さない
 * ```
 *
 * 渦・衝突・出るモンスターは**画面の中央に固定**（シンクロ v3 で決まったこと）。名前が分からない素材は「？」、全部分からなければ数式を出さない。
 */
export const D = 3000;
export const D_NO_RESULT = 2400;
const TERMS = [380, 700];     // 数式: 最初の名前 / 最後の名前
const MORPH = 1000;           // 同時に変身（渦が開く）
const STOP = 100;             // ヒットストップ
const ORBIT = [MORPH + STOP, 1600];
const CLASH = 1600;
const COLLAPSE = [CLASH + STOP, CLASH + STOP + 70];
const BURST = [CLASH + STOP, CLASH + STOP + 240];
const MON = 1750;
const MON_OPEN = 120;
const MON_COLOR = [1810, 1950];
const NAME_HIT = 1950;
const BAND_HIT = 2030;

const RED = '#ff3b30';
const BLUE = '#3d8bff';
const FUSION_PURPLE = '#9b4dff';
const VORTEX_R = 340;
const HOLE = 250;
const SHARDS = 26;
const RAYS = 18;
const TRAIL_PTS = 7;
const NAME_SIZE = 84;          // 1 行に収まるときの文字の大きさ
const LINE_SIZE = [76, 68];     // 2 行 / 3 行以上に分けるときの文字の大きさ
const NAME_MIN = 52;            // 縮めても文字の大きさを 46px より上に保つ
const SINGLE_MIN = 64;          // 1 行でここより小さくなるなら行を分ける
const FORMULA_W = 1300;         // 左右の HUD（手札）に掛けない幅
const FORMULA_Y = 290;          // 紋章の下のフェイズ名に掛からず、カードの上端より上（行を分けたら中心）

/** 止めの時間の写し（変身と衝突の止めの間は渦の回り・破片が止まる） */
const frozen = (t) => {
  if (t < MORPH) return t;
  if (t < MORPH + STOP) return MORPH;
  if (t < CLASH) return t - STOP;
  if (t < CLASH + STOP) return CLASH - STOP;
  return t - 2 * STOP;
};

/** 帯の尺（出るモンスターが無ければ衝突で終わる） */
export const durationOf = (payload) => (payload?.result?.code != null ? D : D_NO_RESULT);

/** 名前を叩き込む時刻（最初は 380、残りは 700 までに等分） */
export function nameTimes(n) {
  if (n <= 1) return [TERMS[0]];
  return Array.from({ length: n }, (_, i) => TERMS[0] + ((TERMS[1] - TERMS[0]) * i) / (n - 1));
}
export const MORPH_AT = MORPH;
export const CLASH_AT = CLASH;

/** 見積もりの幅（全角 1 文字 ≒ 文字の大きさ、半角 ≒ 0.62） */
const estWidth = (text, size) => Array.from(text).reduce((a, c) => a + (c.charCodeAt(0) < 0x100 ? 0.62 : 1), 0) * size;
const truncate = (text) => {
  const cs = Array.from(text);
  const body = cs[cs.length - 1] === '…' ? cs.slice(0, -2) : cs.slice(0, -1);
  return [...body, '…'].join('');
};

/**
 * **数式の並べ方**（純関数）: 素材の名前を `○○ + △△` に。全部分からなければ null、分からない名前は「？」。
 * 1 行で `FORMULA_W` に収めて `SINGLE_MIN` 以上なら 1 行（大きさ `NAME_SIZE`）。そうでなければ名前ごとに行を分け（2 行目からは頭に「+」）、
 * それでも `NAME_MIN` を割る行は名前を後ろから削って「…」を付ける。返すのは `{ size, lines: [[項, …], …] }`（項は名前か '+'）
 */
export function fusionLayout(names) {
  if (!names.length || names.every((s) => !s)) return null;
  const terms = names.map((s) => s || '？');
  const PLUS = 1.1;   // 「+」の項の幅（文字の大きさに対する比。前後の間を含む）
  const lineW = (line, size) => line.reduce((a, x) => a + (x === '+' ? PLUS * size : estWidth(x, size)), 0);
  const single = terms.flatMap((x, i) => (i ? ['+', x] : [x]));
  if ((FORMULA_W / lineW(single, NAME_SIZE)) * NAME_SIZE >= SINGLE_MIN || terms.length === 1) {
    if (terms.length === 1) {
      let x = terms[0];
      while ((FORMULA_W / estWidth(x, NAME_SIZE)) * NAME_SIZE < NAME_MIN && Array.from(x).length > 3) x = truncate(x);
      return { size: NAME_SIZE, lines: [[x]] };
    }
    return { size: NAME_SIZE, lines: [single] };
  }
  const size = terms.length === 2 ? LINE_SIZE[0] : LINE_SIZE[1];
  const lines = terms.map((x, i) => {
    let name = x;
    const line = () => (i ? ['+', name] : [name]);
    while ((FORMULA_W / lineW(line(), size)) * size < NAME_MIN && Array.from(name).length > 3) name = truncate(name);
    return line();
  });
  return { size, lines };
}

function sceneFor(payload) {
  const out = [
    'reset',
    {
      ms: 0,
      grade: {
        contrast: {
          keys: [
            { ms: MORPH - 1, value: 1 }, { ms: MORPH, value: 1.35 }, { ms: MORPH + STOP, value: 1.35, ease: 'hold' }, { ms: MORPH + STOP + 1, value: 1 },
            { ms: CLASH - 1, value: 1 }, { ms: CLASH, value: 1.45 }, { ms: CLASH + STOP, value: 1.45, ease: 'hold' }, { ms: CLASH + STOP + 1, value: 1 },
          ],
        },
        chroma: {
          keys: [
            { ms: MORPH - 1, value: 0 }, { ms: MORPH, value: 0.012 }, { ms: MORPH + 34, value: 0, ease: 'ease_out' },
            { ms: CLASH - 1, value: 0 }, { ms: CLASH, value: 0.016 }, { ms: CLASH + 34, value: 0, ease: 'ease_out' },
          ],
        },
      },
    },
    { ms: MORPH + STOP, shake: { amp: 22, freq: 24, decayMs: 300 } },
    { ms: CLASH + STOP, shake: { amp: 38, freq: 22, decayMs: 440 } },
  ];
  const n = Array.isArray(payload?.materials) ? payload.materials.length : 0;
  nameTimes(n).forEach((t) => out.push({ ms: Math.round(t) + 10, shake: { amp: 9, freq: 28, decayMs: 160 } }));
  if (payload?.result?.code != null) {
    out.push({ ms: NAME_HIT, shake: { amp: 8, freq: 30, decayMs: 160 } });
    out.push({ ms: BAND_HIT, shake: { amp: 14, freq: 26, decayMs: 220 } });
  }
  return out;
}

export default defineEffect({
  situation: 'material_summon_fusion',
  role: 'effect',
  label: '融合召喚の変身バンク（素材と名前の数式 → 渦に吸い込まれて衝突 → 出るモンスター）',
  color: FUSION_PURPLE,
  duration: ({ payload } = {}) => durationOf(payload),
  scene: (ctx) => sceneFor(ctx?.payload),
  sounds: ({ payload } = {}) => {
    const n = Array.isArray(payload?.materials) ? payload.materials.length : 0;
    const out = [{ at: 0, name: 'phase_change', gain: 0.55 }];
    nameTimes(n).forEach((t) => out.push({ at: Math.round(t) - 10, name: 'set_card', gain: 0.7 }));
    out.push({ at: MORPH - 10, name: 'effect', gain: 0.85 });
    out.push({ at: ORBIT[0] + 40, name: 'draw', gain: 0.5 });
    out.push({ at: ORBIT[0] + 300, name: 'draw', gain: 0.5 });
    out.push({ at: CLASH - 15, name: 'attack', gain: 1 });
    if (payload?.result?.code != null) out.push({ at: MON, name: 'summon' });
    return out;
  },

  layers: [{
    id: 'bank',
    label: '素材と名前の数式 → 渦に吸い込まれて衝突 → 出るモンスター',
    space: 'screen',
    script: (ctx) => {
      const { THREE, kit } = ctx;
      const { w: W, h: H } = canvasOf(ctx);
      const { payload } = ctx;
      const p = payload.player === 1 ? 1 : 0;
      const pc = playerColor(ctx, p);
      const materials = Array.isArray(payload.materials) ? payload.materials : [];
      const result = payload.result?.code != null ? payload.result : null;
      const dur = durationOf(payload);
      const rand = seeded(`${ctx.seed}:fusion`);
      const frame = bankFrame(ctx, { dur });
      const { layer } = frame;
      const VX = W / 2;
      const VY = 600;

      // --- 素材のカード（中央の渦の左右の外側。1 枚目が左・ほかが右）
      const n = materials.length;
      const CW = Math.min(230, 1400 / Math.max(1, n) - 60);
      const ROW_Y = VY;
      const lay = sideRow(Math.min(1, n), Math.max(0, n - 1), CW, { W, hole: HOLE });
      const layout = fusionLayout(materials.map((m) => cardName(ctx, m.code)));
      const times = nameTimes(n);
      const row = materialCards(ctx, frame, {
        materials, player: p, xs: lay.xs, y: ROW_Y, cw: CW, rand, hitsOf: (i) => (layout ? [times[i]] : []),
      });
      const CH = row.ch;
      // 渦の周りの公転の出発点（カードの並んだ所から）と、軌跡の色（赤と青を互い違い）
      const orbits = row.items.map((mc, i) => {
        const dx = mc.x - VX; const dy = mc.y - VY;
        const color = i % 2 === 0 ? RED : BLUE;
        const segs = Array.from({ length: TRAIL_PTS - 1 }, () => ({
          m: add(layer(3.5), fill(ctx, color, 1, 1), 3.5), w: add(layer(3.6), fill(ctx, '#ffffff', 1, 1), 3.6),
        }));
        return {
          mc, r0: Math.hypot(dx, dy), a0: Math.atan2(dy, dx), segs,
        };
      });
      const REVS = 1.15;
      const orbitAt = (o, t) => {
        const k = tween(t, ORBIT[0], ORBIT[1], 'power2_in');
        const ka = tween(t, ORBIT[0], ORBIT[1], 'power3_in') * 0.6 + k * 0.4;
        const a = o.a0 + REVS * Math.PI * 2 * ka;
        const r = o.r0 * (1 - k);
        return {
          x: VX + Math.cos(a) * r, y: VY + Math.sin(a) * r, s: 1 - 0.55 * k, rot: 0.9 * ka * Math.PI, k,
        };
      };

      // --- 渦（2 枚の硬い螺旋を逆に回す。外に紫の硬い輪。中心に黒い穴）
      const spiralA = add(layer(2), sprite(ctx, kit.hardSpiralTexture([RED, BLUE], { size: 1024, arms: 6, turns: 1.1, width: 0.42 }), VORTEX_R * 2, VORTEX_R * 2), 2);
      const spiralB = add(layer(2.1), sprite(ctx, kit.hardSpiralTexture([BLUE, RED], { size: 1024, arms: 4, turns: 1.5, width: 0.36 }), VORTEX_R * 1.3, VORTEX_R * 1.3), 2.1);
      const rim = add(layer(2.2), sprite(ctx, kit.hardRingTexture(FUSION_PURPLE, { size: 512, band: 0.07, notches: 16 }), VORTEX_R * 2.12, VORTEX_R * 2.12), 2.2);
      const core = add(layer(2.3), new THREE.Mesh(new THREE.CircleGeometry(1, 24), kit.plain(null, 1)), 2.3);
      core.material.color?.set?.('#050507');
      const burstRing = add(layer(9.5), sprite(ctx, kit.hardRingTexture('#ffffff', { size: 512, band: 0.05, notches: 0 }), VORTEX_R * 2, VORTEX_R * 2), 9.5);

      // --- 衝突: カードの形の閃光・画面の閃光・鋼の破片・放射の硬い線
      const clashFlash = add(layer(9), fill(ctx, '#ffffff', CW * 1.1, CH * 1.1), 9);
      const flash = add(layer(14), place(fill(ctx, '#ffffff', W, H), W / 2, H / 2), 14);
      const shards = Array.from({ length: SHARDS }, (_, k) => {
        const m = new THREE.Mesh(new THREE.CircleGeometry(1, 3), kit.plain(null, 1));
        m.material.color?.set?.(k % 4 === 0 ? '#e6e9ee' : (k % 4 === 1 ? '#c4a0ff' : '#9aa1ad'));
        add(layer(8), m, 8);
        return {
          m, a: rand() * Math.PI * 2, size: 12 + rand() * 20, stretch: 1.5 + rand() * 1.5, speed: 600 + rand() * 1100, spin: (rand() - 0.5) * 26,
        };
      });
      const rays = Array.from({ length: RAYS }, (_, k) => ({
        m: add(layer(8.5), fill(ctx, k % 3 === 0 ? FUSION_PURPLE : '#ffffff', 1, 1), 8.5),
        a: (k / RAYS) * Math.PI * 2 + (rand() - 0.5) * 0.2,
        len: 260 + rand() * 260,
        w: 4 + rand() * 8,
      }));

      // --- 数式（素材の名前。○○ + △△）
      // 行ごとに組む。名前 i の行（i 行目、1 行なら全部）の項はその名前の時刻に叩き込む
      const lineH = layout ? layout.size * 1.02 : 0;
      const rows = layout ? layout.lines.map((line, li) => {
        // 1 行なら名前ごとに順に（「+」は次の名前と同じ時刻）、行を分けたら行 li の項は名前 li の時刻
        let name = layout.lines.length === 1 ? -1 : li;
        const tokens = line.map((text) => {
          const plus = text === '+';
          if (layout.lines.length === 1 && !plus) name += 1;
          const at = times[Math.min(times.length - 1, layout.lines.length === 1 && plus ? name + 1 : name)];
          return { text, size: plus ? Math.round(layout.size * 1.1) : layout.size, at };
        });
        return { y: FORMULA_Y + (li - (layout.lines.length - 1) / 2) * lineH, f: formulaRow(ctx, frame, tokens, { maxW: FORMULA_W, slamFrom: 1.5 }) };
      }) : [];

      // --- 出るモンスター（渦の所＝画面の中央）
      const mon = result ? monsterPlate(ctx, frame, {
        result, player: p, pc, accent: '#6a2bb0', bandColor: FUSION_PURPLE, tag: 'FUSION SUMMON', cx: VX,
      }) : null;

      return {
        root: frame.top,
        update: ({ localMs: t }) => {
          const v = frozen(t);
          frame.update(t);

          // --- 渦: 1000 で開く（止めの間はその大きさ）→ 回る（中心へ流れる向き）→ 1700 で一気に縮んで弾ける
          const open = t < MORPH ? 0 : 0.55 + 0.45 * tween(t, MORPH + STOP, MORPH + STOP + 160, 'expo_out');
          const collapse = 1 - tween(t, COLLAPSE[0], COLLAPSE[1], 'power2_in');
          const vs = open * collapse;
          const spin = Math.max(0, v - MORPH) / 1000;
          const vortexOn = t >= MORPH && collapse > 0.001;
          place(spiralA, VX, VY, { s: Math.max(0.001, vs), rot: spin * 2.4 });
          place(spiralB, VX, VY, { s: Math.max(0.001, vs), rot: -spin * 3.6 });
          place(rim, VX, VY, { s: Math.max(0.001, vs), rot: spin * 0.9 });
          place(core, VX, VY, { s: Math.max(0.001, 34 * vs * (1 + 0.6 * seg(t, ORBIT[0], CLASH))) });
          [spiralA, spiralB, rim, core].forEach((m) => fade(m, vortexOn ? 1 : 0));

          // --- 素材のカード: 並ぶ → 名前ごとに揺れ・縁 → 1000 で閃光 → 渦の周りを回りながら中心へ → 1600 でぶつかって消える
          orbits.forEach((o) => {
            const { mc } = o;
            let pose;
            if (t < ORBIT[0]) {
              const ps = row.pose(mc, t);
              pose = { ...ps };
            } else {
              pose = orbitAt(o, t);
            }
            place(mc.c.mesh, pose.x, pose.y, { s: pose.s, rot: pose.rot });
            fade(mc.c.mesh, t < CLASH ? 1 : 0);
            row.edges(mc, pose.x, pose.y, pose.s, Boolean(pose.lit) && t < MORPH, pose.rot);
            place(mc.flash, mc.x, mc.y);
            fade(mc.flash, t >= MORPH && t < MORPH + 34 ? 0.95 : 0);
            // 後ろの硬い線の軌跡（公転している間だけ。後ろほど細い）
            const pts = Array.from({ length: TRAIL_PTS }, (_, j) => orbitAt(o, Math.max(ORBIT[0], t - j * 34)));
            o.segs.forEach((sgm, j) => {
              const a = pts[j]; const b = pts[j + 1];
              const dx = a.x - b.x; const dy = a.y - b.y;
              const len = Math.hypot(dx, dy);
              const wdt = 34 * (1 - j / TRAIL_PTS) * Math.max(0.6, a.s);
              const on = t >= ORBIT[0] + 20 && t < CLASH && len > 2;
              const rot = Math.atan2(dy, dx);
              place(sgm.m, (a.x + b.x) / 2, (a.y + b.y) / 2, { sx: Math.max(0.001, len + wdt * 0.5), sy: Math.max(0.001, wdt), rot });
              place(sgm.w, (a.x + b.x) / 2, (a.y + b.y) / 2, { sx: Math.max(0.001, len), sy: Math.max(0.001, wdt * 0.3), rot });
              fade(sgm.m, on ? 1 : 0);
              fade(sgm.w, on ? 1 : 0);
            });
          });

          // --- 数式: 叩き込む → 渦が開いたら渦へ吸われて消える
          const suck = tween(t, MORPH + STOP, MORPH + STOP + 220, 'power2_in');
          rows.forEach((r) => r.f.update(t, {
            y: r.y + (VY - r.y) * suck, toX: VX, suck, hideAt: MORPH + STOP + 220,
          }));

          // --- 衝突
          place(clashFlash, VX, VY, { rot: 0.12 });
          fade(clashFlash, t >= CLASH && t < CLASH + 67 ? 1 : 0);
          fade(flash, t >= CLASH && t < CLASH + 34 ? 0.55 : 0);
          const bk = tween(t, BURST[0], BURST[1], 'expo_out');
          place(burstRing, VX, VY, { s: Math.max(0.001, 0.25 + 1.5 * bk), rot: bk });
          fade(burstRing, t >= BURST[0] && t < BURST[1] ? 1 - seg(t, BURST[0] + 100, BURST[1]) : 0);
          const since = Math.max(0, (t - (CLASH + STOP)) / 1000);
          shards.forEach((s) => {
            const k = Math.min(1, since / 0.55);
            const r = s.speed * since;
            s.m.position.set(VX + Math.cos(s.a) * r, VY + Math.sin(s.a) * r + 1200 * since * since, 0);
            s.m.rotation.z = s.a + s.spin * since;
            s.m.scale.set(s.size * s.stretch, s.size, 1);
            fade(s.m, t >= CLASH + STOP && k < 1 ? 1 - k * k : 0);
          });
          const rk = seg(t, CLASH + STOP, CLASH + STOP + 160);
          rays.forEach((r) => {
            const d0 = 60 + 500 * tween(t, CLASH + STOP, CLASH + STOP + 160, 'expo_out');
            const len = r.len * (1 - rk);
            const mid = d0 + len / 2;
            place(r.m, VX + Math.cos(r.a) * mid, VY + Math.sin(r.a) * mid, { sx: Math.max(0.001, len), sy: Math.max(0.001, r.w * (1 - rk)), rot: r.a });
            fade(r.m, t >= CLASH + STOP && rk < 1 ? 1 : 0);
          });

          // --- 出るモンスター
          mon?.update(t, {
            at: MON, open: MON_OPEN, colorAt: MON_COLOR, nameAt: NAME_HIT, bandAt: BAND_HIT,
          });
        },
      };
    },
  }],
}, 'material_summon_fusion_effect.js');
